import { attributeTypes } from 'utils';
import { hasInlineFields, inlineFieldsToText } from './inlineFields';

/**
 * Which attributes a read-only note view draws — same rule as the PDF
 * (`isPrintableAttribute` in reportMicroservice/src/util/emptyAttribute.util.ts).
 * Mirrored in members_client/src/fragments/formRenderer/utils/printableAttribute.js;
 * keep the three in step.
 *
 * View mode only. Left out entirely, label included:
 *  - a SESSION_DATA_TABLE with no rows (no data collected, session not
 *    completed, or no session) — its empty-state message is not shown either;
 *  - a Goal Mode RICH_TEXT with no goal picked (`goalModeGoalIds` empty, so
 *    `value` is still the template) or whose text is empty once filled.
 *  - a Goal Mode TABLE with no goal picked: no row tagged `_goalId`, so the
 *    only row is the template with its goal tokens still literal.
 *  - a RICH_TEXT holding inline fields / Goal blocks with nothing left once the
 *    unfilled ones are dropped (`inlineFieldsToText`).
 * Every other attribute, plain RICH_TEXT and TABLE included, is drawn.
 */

/** No text once tags, `&nbsp;` and whitespace are removed, and no image. */
export const isEmptyRichText = (html) => {
    if (html === null || html === undefined) return true;
    const text = String(html);
    if (/<img\b/i.test(text)) return false;
    return (
        text
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;|&#160;|&#xa0;/gi, '')
            .replace(/[\s ]/g, '') === ''
    );
};

export const isPrintableAttribute = (attribute) => {
    if (!attribute) return false;

    if (attribute.type === attributeTypes.SESSION_DATA_TABLE) {
        return Array.isArray(attribute.value) && attribute.value.length > 0;
    }

    if (attribute.type === attributeTypes.RICH_TEXT && attribute.properties?.goalMode) {
        if (!attribute.goalModeGoalIds?.length) return false;
        return !isEmptyRichText(attribute.value);
    }

    if (attribute.type === attributeTypes.RICH_TEXT && hasInlineFields(attribute.value)) {
        return !isEmptyRichText(inlineFieldsToText(attribute.value));
    }

    if (attribute.type === attributeTypes.TABLE && attribute.properties?.goalMode) {
        return Array.isArray(attribute.value) && attribute.value.some((row) => row?._goalId);
    }

    return true;
};
